import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
	FiActivity,
	FiAlertTriangle,
	FiCheckCircle,
	FiExternalLink,
	FiKey,
	FiLock,
	FiMail,
	FiShield,
	FiTrash2,
	FiUnlock,
	FiX,
} from "react-icons/fi";
import { usePermissions } from "../../Auth/PermissionsContext";
import { expandGrants } from "../../Auth/permissionTokens";
import GatedButton from "../../Components/GatedButton";
import { UserAvatar } from "../../Components/UserAvatar";
import { groupRolesByFamily } from "../../Helper/roleFamilies";
import { RoleBadgeChip } from "../../Components/RoleBadges";
import { UserActivityJournal } from "../../Components/Account/UserActivityJournal";
import { getUserActivity, type ActivityFilter } from "../../Components/Account/accountClient";
import { ForcePasswordModal } from "./ForcePasswordModal";
import { impactOfRoles, providerOf, type UserPerson } from "./userGrouping";
import { usersClient, type AdminUser, type AssignableRole } from "./usersClient";

/**
 * Fiche « Gérer » d'une personne : TOUT ce que l'annuaire et le CMS savent d'elle, et toutes les
 * actions d'administration au même endroit. Une personne peut avoir plusieurs identités (e-mail /
 * mot de passe ET Google sur la même adresse) : la fiche les montre toutes et les rôles s'appliquent
 * à chacune, pour que ses droits soient les mêmes quel que soit le bouton de connexion utilisé.
 *
 * Trois onglets : Rôles et accès (rôles, ce qu'ils permettent, nom d'affichage), Sécurité
 * (identités, blocage, mot de passe, invitation, suppression), Activité (journal paginé + filtré).
 * Le serveur reste l'autorité (règles de sous-ensemble, super-admin, soi-même : 403 sinon).
 */

const dateFmt = new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" });
const fmt = (iso?: string | null) => (iso ? dateFmt.format(new Date(iso)) : "-");
const WRITE_TOKEN = "feature:users:write";

type Tab = "roles" | "security" | "activity";

type Props = {
	person: UserPerson;
	roles: AssignableRole[];
	isAuth0: boolean;
	onClose: () => void;
	/** Après une mutation (rôles, blocage, suppression…) : le parent recharge la liste. */
	onChanged: () => void;
};

const Fact: React.FC<{ label: string; children: React.ReactNode; title?: string }> = ({ label, children, title }) => (
	<div className="flex items-baseline justify-between gap-3 py-1.5 text-xs" title={title}>
		<dt className="shrink-0 text-slate-400">{label}</dt>
		<dd className="min-w-0 truncate text-right font-medium text-slate-700">{children}</dd>
	</div>
);

const ProviderChip: React.FC<{ id: string }> = ({ id }) => {
	const p = providerOf(id);
	return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.social ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-600"}`}>{p.label}</span>;
};

export const ManageUserModal: React.FC<Props> = ({ person, roles, isAuth0, onClose, onChanged }) => {
	const { can, permissions: callerTokens, catalog, identity } = usePermissions();
	const canWrite = can(WRITE_TOKEN);
	const canSeeActivity = can("feature:user-activity:read");
	const isSelf = (identity.email ?? "").trim().toLowerCase() === (person.email ?? "").trim().toLowerCase();

	const [tab, setTab] = useState<Tab>("roles");
	const [name, setName] = useState(person.name);
	const [selected, setSelected] = useState<Set<string>>(new Set(person.roles));
	const [saving, setSaving] = useState(false);
	const [busyId, setBusyId] = useState<string | null>(null);
	const [forcing, setForcing] = useState<AdminUser | null>(null);
	const [refreshKey, setRefreshKey] = useState(0);

	// La fiche suit la personne rechargée par le parent après chaque action.
	useEffect(() => {
		setName(person.name);
		setSelected(new Set(person.roles));
	}, [person]);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose]);

	const rolesByKey = useMemo(() => new Map(roles.map((r) => [r.key, r])), [roles]);
	const roleBadge = (key: string) => rolesByKey.get(key)?.badge ?? null;
	const impactNow = useMemo(() => impactOfRoles(person.roles, rolesByKey, catalog), [person.roles, rolesByKey, catalog]);
	const impactDraft = useMemo(() => impactOfRoles(Array.from(selected), rolesByKey, catalog), [selected, rolesByKey, catalog]);

	// Règle de sous-ensemble à l'attribution (miroir du serveur) : un rôle n'est proposable que si son
	// set expansé est inclus dans celui de l'appelant ; un rôle déjà porté reste toujours retirable.
	const excessByRole = useMemo(() => {
		const m = new Map<string, string[]>();
		if (!catalog) return m;
		const mine = callerTokens ?? new Set<string>();
		for (const r of roles) {
			m.set(
				r.key,
				Array.from(expandGrants(catalog, r.permissions ?? []))
					.filter((t) => !mine.has(t))
					.sort()
			);
		}
		return m;
	}, [roles, catalog, callerTokens]);
	const originallyHeld = useMemo(() => new Set(person.roles.map((r) => r.trim().toLowerCase())), [person.roles]);

	const rolesDirty = useMemo(() => {
		const a = Array.from(selected).sort().join(",");
		const b = [...person.roles].sort().join(",");
		return a !== b || (name ?? "").trim() !== (person.name ?? "").trim();
	}, [selected, person.roles, name, person.name]);

	const toggleRole = (key: string) =>
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});

	// ------------------------------------------------------------ actions

	/** Applique le nom et le jeu de rôles à CHAQUE identité de la personne. */
	const saveRoles = async () => {
		setSaving(true);
		const rolesList = Array.from(selected);
		let failed = 0;
		for (const u of person.identities) {
			try {
				await usersClient.update(u.id, { name: name.trim() || undefined, blocked: u.blocked, roles: rolesList });
			} catch (e: any) {
				failed++;
				toast.error(`${providerOf(u.id).label} : ${e?.response?.data?.message ?? "enregistrement impossible."}`);
			}
		}
		setSaving(false);
		if (failed === 0) toast.success(person.identities.length > 1 ? `Rôles appliqués aux ${person.identities.length} identités.` : "Rôles mis à jour.");
		onChanged();
	};

	const setBlocked = async (u: AdminUser, blocked: boolean) => {
		if (blocked && !window.confirm(`Bloquer la connexion ${providerOf(u.id).label} de « ${person.email} » ?`)) return;
		setBusyId(u.id);
		try {
			await usersClient.update(u.id, { name: u.name ?? undefined, blocked, roles: u.roles });
			toast.success(blocked ? "Connexion bloquée." : "Connexion rétablie.");
			onChanged();
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? "Action impossible.");
		} finally {
			setBusyId(null);
		}
	};

	const setBlockedAll = async (blocked: boolean) => {
		if (!window.confirm(blocked ? `Bloquer TOUTES les connexions de « ${person.email} » (${person.identities.length}) ?` : `Rétablir toutes les connexions de « ${person.email} » ?`)) return;
		setSaving(true);
		for (const u of person.identities) {
			try {
				await usersClient.update(u.id, { name: u.name ?? undefined, blocked, roles: u.roles });
			} catch (e: any) {
				toast.error(`${providerOf(u.id).label} : ${e?.response?.data?.message ?? "action impossible."}`);
			}
		}
		setSaving(false);
		toast.success(blocked ? "Toutes les connexions sont bloquées." : "Connexions rétablies.");
		onChanged();
	};

	const removeIdentity = async (u: AdminUser) => {
		const last = person.identities.length === 1;
		const msg = last
			? `Supprimer définitivement le compte « ${person.email} » ? Cette action est irréversible.`
			: `Supprimer l'identité ${providerOf(u.id).label} de « ${person.email} » ? La personne gardera ses autres moyens de connexion.`;
		if (!window.confirm(msg)) return;
		setBusyId(u.id);
		try {
			await usersClient.remove(u.id);
			toast.success(last ? "Compte supprimé." : "Identité supprimée.");
			onChanged();
			if (last) onClose();
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? "Suppression impossible.");
		} finally {
			setBusyId(null);
		}
	};

	const resendInvite = async (u: AdminUser) => {
		setBusyId(u.id);
		try {
			await usersClient.resendInvite(u.id);
			toast.success("E-mail de définition du mot de passe renvoyé.");
			setRefreshKey((k) => k + 1);
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? "Envoi impossible.");
		} finally {
			setBusyId(null);
		}
	};

	const primary = person.identities[0];
	const fetchActivity = useCallback((page: number, pageSize: number, filter: ActivityFilter) => getUserActivity(primary.id, page, pageSize, filter), [primary.id]);

	const impactChip = (impact: ReturnType<typeof impactOfRoles>) =>
		impact.admin ? (
			<span className="inline-flex items-center gap-1 rounded-full bg-[#0F2849] px-2 py-0.5 text-[11px] font-semibold text-white">
				<FiShield className="h-3 w-3" aria-hidden /> Administration complète
			</span>
		) : impact.production ? (
			<span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" title={`${impact.promoteTypes} type(s) de contenu`}>
				<FiCheckCircle className="h-3 w-3" aria-hidden /> Peut mettre en production
			</span>
		) : impact.publish ? (
			<span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Publie en preview seulement</span>
		) : (
			<span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Aucune mise en production</span>
		);

	const tabBtn = (key: Tab, label: string, icon: React.ReactNode) => (
		<button
			key={key}
			type="button"
			role="tab"
			aria-selected={tab === key}
			onClick={() => setTab(key)}
			className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${tab === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
		>
			{icon}
			{label}
		</button>
	);

	return (
		<div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true" aria-label={`Gérer ${person.name}`}>
			<div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
				{/* En-tête : identité, statut, impact, fermeture. */}
				<div className="flex items-start gap-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
					<UserAvatar name={person.name} id={primary.id} avatarKey={person.avatarKey} pictureUrl={person.picture} sizeClass="h-14 w-14" textClass="text-lg" />
					<div className="min-w-0 flex-1">
						<div className="flex flex-wrap items-center gap-2">
							<h2 className="truncate text-lg font-bold text-slate-900">{person.name}</h2>
							{person.blocked ? (
								<span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-600">Bloqué</span>
							) : person.anyBlocked ? (
								<span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Partiellement bloqué</span>
							) : (
								<span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">Actif</span>
							)}
							{impactChip(impactNow)}
							{isSelf && <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700">C'est vous</span>}
						</div>
						<p className="mt-0.5 truncate text-sm text-slate-500">{person.email}</p>
						<div className="mt-1.5 flex flex-wrap items-center gap-1.5">
							{person.identities.map((u) => (
								<ProviderChip key={u.id} id={u.id} />
							))}
							<span className="text-[11px] text-slate-400">
								{person.identities.length} identité{person.identities.length > 1 ? "s" : ""}
							</span>
							{person.roles.map((r) => {
								const b = roleBadge(r);
								return b ? <RoleBadgeChip key={r} badge={b} className="px-2 text-[11px]" /> : null;
							})}
						</div>
					</div>
					<Link to={`/account/${primary.id}`} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50" title="Ouvrir la page de profil">
						Profil <FiExternalLink className="h-3.5 w-3.5" aria-hidden />
					</Link>
					<button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Fermer">
						<FiX className="h-5 w-5" />
					</button>
				</div>

				<div className="flex min-h-0 flex-1">
					{/* Colonne gauche : les faits. */}
					<aside className="hidden w-64 shrink-0 overflow-y-auto border-r border-slate-100 bg-slate-50/60 px-4 py-4 md:block">
						<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Compte</p>
						<dl className="mt-1 divide-y divide-slate-100">
							<Fact label="Créé le">{fmt(person.createdAt)}</Fact>
							<Fact label="Dernière connexion">{fmt(person.lastLogin)}</Fact>
							<Fact label="Connexions">{person.loginsCount != null ? person.loginsCount.toLocaleString("fr-FR") : "-"}</Fact>
							<Fact label="Dernière IP" title="Adresse IP de la dernière connexion (annuaire)">
								{person.lastIp ?? "-"}
							</Fact>
							<Fact label="E-mail vérifié">{person.emailVerified == null ? "-" : person.emailVerified ? "Oui" : "Non"}</Fact>
							<Fact label="Mot de passe">
								{!person.hasPassword ? "Connexion sociale" : person.lastPasswordReset ? fmt(person.lastPasswordReset) : <span className="text-amber-700">Jamais changé</span>}
							</Fact>
							<Fact label="Annuaire">{isAuth0 ? "Auth0" : "Base locale"}</Fact>
						</dl>
						<p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Identités</p>
						<ul className="mt-1 space-y-2">
							{person.identities.map((u) => (
								<li key={u.id} className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
									<div className="flex items-center justify-between gap-2">
										<ProviderChip id={u.id} />
										{u.blocked ? <span className="text-[10px] font-semibold text-rose-600">bloquée</span> : <span className="text-[10px] font-semibold text-emerald-600">active</span>}
									</div>
									<p className="mt-1 truncate font-mono text-[10px] text-slate-400" title={u.id}>
										{u.id}
									</p>
									<p className="text-[10px] text-slate-500">Vu {u.lastLogin ? fmt(u.lastLogin) : "jamais"}</p>
								</li>
							))}
						</ul>
					</aside>

					{/* Colonne droite : onglets. */}
					<div className="flex min-h-0 flex-1 flex-col">
						<div className="flex items-center gap-1 border-b border-slate-100 bg-slate-50 px-4 py-2" role="tablist">
							{tabBtn("roles", "Rôles et accès", <FiShield className="h-4 w-4" aria-hidden />)}
							{tabBtn("security", "Sécurité", <FiLock className="h-4 w-4" aria-hidden />)}
							{tabBtn("activity", "Activité", <FiActivity className="h-4 w-4" aria-hidden />)}
						</div>

						{tab === "roles" && (
							<div className="flex min-h-0 flex-1 flex-col">
								<div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
									<label className="block">
										<span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Nom affiché</span>
										<input value={name} onChange={(e) => setName(e.target.value)} disabled={!canWrite} className="w-full max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50" />
									</label>
									<div>
										<div className="mb-2 flex flex-wrap items-center justify-between gap-2">
											<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rôles</p>
											<div className="flex items-center gap-2 text-xs text-slate-500">
												<span>Après enregistrement :</span>
												{impactChip(impactDraft)}
											</div>
										</div>
										{person.identities.length > 1 && (
											<p className="mb-2 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-xs text-sky-800">
												Cette personne a {person.identities.length} identités : les rôles ci-dessous seront appliqués à chacune, pour que ses droits soient identiques quel que soit son mode de connexion.
											</p>
										)}
										{groupRolesByFamily(roles).map((family) => (
											<div key={family.key} className="mb-3">
												<p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{family.label}</p>
												<div className="grid gap-2 sm:grid-cols-2">
											{family.roles.map((r) => {
												const excess = excessByRole.get(r.key) ?? [];
												const blocked = !originallyHeld.has(r.key.trim().toLowerCase()) && excess.length > 0;
												const checked = selected.has(r.key);
												return (
													<label
														key={r.key}
														title={blocked ? `Accorde des permissions que vous ne détenez pas : ${excess.slice(0, 3).join(", ")}${excess.length > 3 ? ` +${excess.length - 3}` : ""}` : r.description ?? undefined}
														className={`flex items-start gap-2 rounded-xl border p-3 text-sm transition ${
															blocked || !canWrite ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60" : checked ? "cursor-pointer border-sky-300 bg-sky-50" : "cursor-pointer border-slate-200 hover:bg-slate-50"
														}`}
													>
														<input type="checkbox" className="mt-0.5" disabled={blocked || !canWrite} checked={checked} onChange={() => toggleRole(r.key)} />
														<span className="min-w-0">
															<span className="flex items-center gap-1.5 font-medium text-slate-800">
																{r.badge ? <RoleBadgeChip badge={r.badge} className="px-2 text-[11px]" /> : null}
																{r.name}
															</span>
															{r.description && <span className="mt-0.5 block text-xs text-slate-400">{r.description}</span>}
															{blocked && <span className="block text-[11px] font-medium text-slate-400">Rôle non attribuable avec vos permissions</span>}
														</span>
													</label>
												);
											})}
												</div>
											</div>
										))}
									</div>
								</div>
								<div className="flex items-center justify-between gap-2 border-t border-slate-100 px-5 py-3">
									<span className="text-xs text-slate-400">{rolesDirty ? "Modifications non enregistrées" : "À jour"}</span>
									<div className="flex gap-2">
										<button type="button" onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
											Fermer
										</button>
										<GatedButton token={WRITE_TOKEN} onClick={saveRoles} disabled={saving || !rolesDirty} className="rounded-xl bg-[#0095DB] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#007cb8] disabled:opacity-50">
											{saving ? "Enregistrement…" : "Enregistrer les rôles"}
										</GatedButton>
									</div>
								</div>
							</div>
						)}

						{tab === "security" && (
							<div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
								{isSelf && (
									<p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
										<FiAlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
										C'est votre propre compte : le blocage, la suppression et le forçage de mot de passe sont refusés par le serveur sur soi-même (passez par « Mon compte »).
									</p>
								)}
								<div className="flex flex-wrap items-center gap-2">
									{person.anyBlocked ? (
										<GatedButton token={WRITE_TOKEN} onClick={() => setBlockedAll(false)} disabled={saving || isSelf} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
											<FiUnlock className="h-3.5 w-3.5" aria-hidden /> Rétablir toutes les connexions
										</GatedButton>
									) : (
										<GatedButton token={WRITE_TOKEN} onClick={() => setBlockedAll(true)} disabled={saving || isSelf} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50">
											<FiLock className="h-3.5 w-3.5" aria-hidden /> Bloquer toutes les connexions
										</GatedButton>
									)}
								</div>
								<ul className="space-y-3">
									{person.identities.map((u) => {
										const p = providerOf(u.id);
										const busy = busyId === u.id;
										const dbIdentity = isAuth0 && u.hasPassword !== false;
										return (
											<li key={u.id} className="rounded-xl border border-slate-200 p-4">
												<div className="flex flex-wrap items-center gap-2">
													<ProviderChip id={u.id} />
													<span className="truncate font-mono text-[11px] text-slate-400" title={u.id}>
														{u.id}
													</span>
													<span className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold ${u.blocked ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>{u.blocked ? "Bloquée" : "Active"}</span>
												</div>
												<dl className="mt-2 grid grid-cols-2 gap-x-4 text-xs sm:grid-cols-4">
													<div>
														<dt className="text-slate-400">Créée le</dt>
														<dd className="font-medium text-slate-700">{fmt(u.createdAt)}</dd>
													</div>
													<div>
														<dt className="text-slate-400">Dernière connexion</dt>
														<dd className="font-medium text-slate-700">{fmt(u.lastLogin)}</dd>
													</div>
													<div>
														<dt className="text-slate-400">Connexions</dt>
														<dd className="font-medium text-slate-700">{u.loginsCount ?? "-"}</dd>
													</div>
													<div>
														<dt className="text-slate-400">Mot de passe</dt>
														<dd className="font-medium text-slate-700">{u.hasPassword === false ? p.label : u.lastPasswordReset ? fmt(u.lastPasswordReset) : <span className="text-amber-700">Jamais changé</span>}</dd>
													</div>
												</dl>
												<div className="mt-3 flex flex-wrap items-center gap-2">
													{u.blocked ? (
														<GatedButton token={WRITE_TOKEN} onClick={() => setBlocked(u, false)} disabled={busy || isSelf} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
															<FiUnlock className="h-3.5 w-3.5" aria-hidden /> Rétablir
														</GatedButton>
													) : (
														<GatedButton token={WRITE_TOKEN} onClick={() => setBlocked(u, true)} disabled={busy || isSelf} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
															<FiLock className="h-3.5 w-3.5" aria-hidden /> Bloquer
														</GatedButton>
													)}
													{dbIdentity && (
														<GatedButton token={WRITE_TOKEN} onClick={() => resendInvite(u)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" title="Renvoyer l'e-mail de définition du mot de passe">
															<FiMail className="h-3.5 w-3.5" aria-hidden /> E-mail de mot de passe
														</GatedButton>
													)}
													{dbIdentity && (
														<GatedButton token={WRITE_TOKEN} onClick={() => setForcing(u)} disabled={busy || isSelf} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
															<FiKey className="h-3.5 w-3.5" aria-hidden /> Forcer un mot de passe
														</GatedButton>
													)}
													<GatedButton token={WRITE_TOKEN} onClick={() => removeIdentity(u)} disabled={busy || isSelf} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50">
														<FiTrash2 className="h-3.5 w-3.5" aria-hidden /> {person.identities.length > 1 ? "Supprimer cette identité" : "Supprimer le compte"}
													</GatedButton>
												</div>
											</li>
										);
									})}
								</ul>
								{!isAuth0 && <p className="text-xs text-slate-400">Annuaire local : les actions de mot de passe et d'invitation n'existent qu'avec Auth0.</p>}
							</div>
						)}

						{tab === "activity" && (
							<div className="flex min-h-0 flex-1 flex-col">
								<UserActivityJournal fetchPage={fetchActivity} allowed={canSeeActivity} compact refreshKey={refreshKey} />
							</div>
						)}
					</div>
				</div>
			</div>

			{forcing && <ForcePasswordModal user={forcing} onClose={() => setForcing(null)} onDone={() => setRefreshKey((k) => k + 1)} />}
		</div>
	);
};

export default ManageUserModal;
